"use client";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@restomat/components/ui";
import { cn } from "@restomat/shared-utils/utils";
import { useTranslations } from "next-intl";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import type { ReactNode } from "react";
import { cardReveal } from "./animations";

export default function SmallFeatureCard({
  rowStart = undefined,
  rowEnd = undefined,
  colSpan = undefined,
  className = "",
  title,
  description,
  content,
  preparing = false,
}: {
  rowStart?: number;
  rowEnd?: number;
  colSpan?: number;
  className?: string;
  title: string;
  description: string;
  content: ReactNode;
  preparing?: boolean;
}) {
  const t = useTranslations("landing.automation");
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, amount: "some" });

  return (
    <motion.div
      ref={cardRef}
      className={cn("break-inside-avoid", className)}
      style={{
        gridRow: rowStart && rowEnd ? `${rowStart} / ${rowEnd}` : undefined,
        gridColumn: colSpan ? `span ${colSpan}` : undefined,
      }}
      variants={cardReveal}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <Card variant={"light"}>
        <CardHeader>
          <div className={"flex w-full overflow-visible justify-end h-0"}>
            {preparing && (
              <motion.div
                className={
                  "bg-accent px-4 py-2 text-xs md:text-sm font-black uppercase border-foreground border-2 rounded-full shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] md:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transform rotate-2 text-foreground absolute z-10 -mt-8 -mr-8"
                }
                initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
                animate={
                  isInView
                    ? { opacity: 1, scale: 1, rotate: 2 }
                    : { opacity: 0, scale: 0.5, rotate: -10 }
                }
                transition={{
                  delay: 0.3,
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
              >
                {t("preparing")}
              </motion.div>
            )}
          </div>
          <CardTitle className={"text-base md:text-lg font-black"}>
            {title}
          </CardTitle>
          <p className={"mt-2 text-sm md:text-base"}>{description}</p>
        </CardHeader>
        <CardContent>
          <div className={""}>{content}</div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
